import ReactDOM from 'react-dom';
import React, { useEffect, useState } from 'react';
import {
  EditIcon,
  MessageSquare,
  PinUnpin,
  Trash,
  VerticalDots,
} from '../../../../../assets/images';
import makeToast from '../../../../../components/toaster';
import { truncateData } from '../../../../../services';
import StyledTooltip from '../../../../../components/tooltip';
import { ChatHistoryCardProps } from './chat-history-card.props';
import { DeleteChatSession, PinUnpinChatSession } from '../../services/chat';
import { Session } from '../../pages/chat-screen/chat-screen.props';

function ChatHistoryCard({
  state,
  dispatch,
  sessions,
  sessionId,
  actionMenuRef,
  handleGetSession,
  inputSessionNameRef,
  handleNewSession,
}: ChatHistoryCardProps) {
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const scrollableDiv = document.getElementById('scrollableDiv');

    const handleScroll = () => {
      dispatch({
        type: 'SET_ACTIVE_SESSION',
        payload: null,
      });
    };

    if (scrollableDiv) {
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [dispatch]);

  const toggleDropdown = (sessionIds: number, event: React.MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    dispatch({
      type: 'SET_ACTIVE_SESSION',
      payload: state.activeSession === sessionIds ? null : sessionIds,
    });
  };

  const handleEditSessionName = (index: number) => {
    const session = sessions?.find((s: Session) => s.id === index);
    dispatch({
      type: 'SET_EDIT_SESSION_INDEX',
      payload: index,
    });
    dispatch({
      type: 'SET_ACTIVE_SESSION',
      payload: null,
    });
    if (session) {
      dispatch({
        type: 'SET_EDITED_SESSION',
        payload: { id: session.id, name: session.name || '', pinned: session.pinned },
      });
    }
  };

  const handleSessionName = (e: React.ChangeEvent<HTMLInputElement>, item: Session) => {
    const newName = e.target.value;
    if (newName.length >= 90) {
      return;
    }
    dispatch({
      type: 'SET_EDITED_SESSION',
      payload: { id: item.id, name: newName, pinned: item.pinned } as Session,
    });
  };

  const handlePinUnpinSession = (session: Session) => {
    const pinned = session.pinned === 0 ? 1 : 0;
    PinUnpinChatSession(session)
      .then((res: { status: number }) => {
        if (res?.status === 200) {
          const message =
            pinned === 1 ? 'Session pinned successfully' : 'Session unpinned successfully';
          makeToast('success', message);
          dispatch({
            type: 'SET_SESSION_HISTORY',
            payload: {
              search: '',
              limit: 10,
              offset: 0,
            },
          });
          dispatch({
            type: 'TOGGLE_DATA_CHANGED',
          });
          dispatch({
            type: 'SET_ACTIVE_SESSION',
            payload: null,
          });
          dispatch({
            type: 'SET_PIN_SESSION_HISTORY',
            payload: { search: '' },
          });
        }
      })
      .catch(() => {
        makeToast('error', 'Something went wrong');
      });
  };

  const handleDeleteSession = (id: number) => {
    // Find the session being deleted to get its provider_id
    const deletedSession = sessions.find((session: Session) => session.id === id);
    const deletedProviderId = deletedSession?.provider_id;

    DeleteChatSession(id)
      .then((res: { status: number; data: { message: string } }) => {
        if (res?.status === 200) {
          makeToast('success', res?.data?.message);
          dispatch({
            type: 'SET_SESSION_HISTORY',
            payload: {
              search: '',
              limit: 10,
              offset: 0,
            },
          });
          dispatch({
            type: 'SET_PIN_SESSION_HISTORY',
            payload: { search: '' },
          });
          dispatch({
            type: 'TOGGLE_DATA_CHANGED',
          });
          dispatch({
            type: 'SET_ACTIVE_SESSION',
            payload: null,
          });

          // Clear file-related data from localStorage for the deleted session's provider
          if (deletedProviderId !== undefined) {
            const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
            const updatedChat = existingChat.map(
              (item: { provider_id: number; session_id: string; data: object }) => {
                // If this is the provider of the deleted session and session_id matches
                if (
                  item.provider_id === Number(deletedProviderId) &&
                  item.session_id === id.toString()
                ) {
                  return {
                    ...item,
                    session_id: null,
                    data: {
                      ...item.data,
                      chatData: [],
                      file_id: undefined,
                      has_document: false,
                      use_document: false,
                    },
                  };
                }
                return item;
              },
            );
            localStorage.setItem('chat', JSON.stringify(updatedChat));
          }
        }
      })
      .catch((err: { response: { data: { message: string } } }) => {
        makeToast('error', err?.response?.data?.message);
      });

    // Handle active session deletion
    if (sessionId === id) {
      const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
      const activeSession = existingChat.find(
        (item: { session_id: string }) => item.session_id === id.toString(),
      );
      const hasChatData = activeSession?.data?.chatData?.length > 0;

      if (hasChatData) {
        handleNewSession();
      } else {
        const updatedChat = existingChat.map((item: { session_id: string; data: object }) => {
          if (item.session_id === id.toString()) {
            return {
              ...item,
              session_id: null,
              data: {
                ...item.data,
                chatData: [],
                file_id: undefined,
                has_document: false,
                use_document: false,
              },
            };
          }
          return item;
        });
        localStorage.setItem('chat', JSON.stringify(updatedChat));
        dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
      }
    }
  };

  const providerStyles: Record<string, string> = {
    openai: 'bg-green-200 text-green-800',
    anthropic: 'bg-blue-200 text-blue-800',
    google: 'bg-yellow-200 text-yellow-800',
  };

  return (
    <ul className="flex flex-col justify-center items-center space-y-1 mb-2">
      {sessions?.map((item: Session) => {
        return (
          <div key={item.id} className="w-full">
            <div
              className={`group flex items-start justify-between w-full px-2 py-1.5 rounded-md transition-colors duration-200 cursor-pointer ${
                state.activeSession === item.id || sessionId === item.id
                  ? 'bg-[#0ABBC7]/20'
                  : 'hover:bg-[#0ABBC7]/20'
              }
              `}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                // Don't trigger if clicking on the 3 dots menu button or its parent
                if (
                  (e.target as HTMLElement).closest('button[type="button"]') ||
                  (e.target as HTMLElement).tagName === 'BUTTON'
                ) {
                  return;
                }
                e.stopPropagation();
                handleGetSession(item.id, item.provider_name, item?.provider_id);
              }}
              onKeyDown={(e) => {
                // Don't trigger row action when typing in the session name input (e.g. space)
                if ((e.target as HTMLElement).closest('input')) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleGetSession(item.id, item.provider_name, item?.provider_id);
                }
              }}
            >
              <div className="flex items-start gap-x-1.5">
                {state.editSessionIndex === item.id ? (
                  <>
                    <img src={MessageSquare} alt="Session Icon" />
                    <input
                      ref={inputSessionNameRef}
                      type="text"
                      value={
                        state.editedSession && state.editedSession.id === item.id
                          ? state.editedSession.name
                          : item.name || ''
                      }
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleSessionName(e, item)}
                      className="ml-2 p-1 rounded-md bg-gray-200 w-72 text-black"
                    />
                  </>
                ) : (
                  <>
                    <img src={MessageSquare} alt="Session Icon" className="mt-1" />
                    <div className="flex flex-col">
                      <StyledTooltip title={item.name} maxWidth={230}>
                        <span className="font-light">
                          {truncateData(item.name, 20) ?? 'Untitled Session'}
                        </span>
                      </StyledTooltip>
                      <StyledTooltip title={item.description} maxWidth={230}>
                        <span className="font-extralight">
                          {truncateData(item.description, 25)}
                        </span>
                      </StyledTooltip>
                      <div className="flex items-center gap-x-4 text-xs mt-1">
                        <span
                          className={`font-bold px-2 py-1 rounded-sm ${
                            providerStyles[item.provider_name?.toLowerCase()] ||
                            'bg-gray-200 text-gray-800'
                          }`}
                        >
                          {item.provider_name}
                        </span>
                        <span>{item.model_name}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="relative flex items-center">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown(item.id, e);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  type="button"
                  data-dropdown-trigger="true"
                >
                  <img src={VerticalDots} className="rotate-90 px-2 mr-2" alt="vertical-dots" />
                </button>
                {state.activeSession === item.id &&
                  ReactDOM.createPortal(
                    <div
                      className="w-32 bg-white text-black rounded-sm shadow-lg z-[10000]"
                      ref={actionMenuRef}
                      data-dropdown-menu="true"
                      style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: `${dropdownPosition.left - 100}px`,
                      }}
                    >
                      <button
                        className="flex items-center w-full pl-2 py-2 gap-2 border-b-2 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSessionName(item.id);
                        }}
                        type="button"
                      >
                        <img src={EditIcon} height={15} width={15} alt="edit-icon" />
                        <span className="text-sm">Edit</span>
                      </button>
                      <button
                        className="flex items-center w-full pl-2 py-2 gap-2 border-b-2 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(item.id);
                        }}
                        type="button"
                      >
                        <img src={Trash} height={15} width={15} alt="trash" />
                        <span className="text-sm">Delete</span>
                      </button>
                      <button
                        className="flex items-center w-full pl-2 py-2 gap-2 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePinUnpinSession(item);
                        }}
                        type="button"
                      >
                        <img src={PinUnpin} height={20} width={15} alt="PinUnpin" />
                        <span className="text-sm">{item?.pinned === 0 ? 'Pin' : 'Unpin'}</span>
                      </button>
                    </div>,
                    document.getElementById('dropdown-root')!,
                  )}
              </div>
            </div>
          </div>
        );
      })}
    </ul>
  );
}

export default ChatHistoryCard;
